import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled'
import { mediaQueries } from 'styled/core'

import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { actionCreators, GettyActionCreators } from '../../../actions'

import { GettyStore } from '../../../store'

import { visitorTypeSchema } from '../../../schemas/visitor-type'

import { connect } from 'containers/store'
import { sharedActionCreators } from 'containers/shared'

import { Fieldset } from 'components/ui/fieldset'
import { FooterEditEntity, FOOTER_HEIGHT } from 'components/ui/footer'
import { NAVBAR_HEIGHT } from 'components/ui/primary-navigation'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { StatusGuard } from 'components/ui/status-guard'
import { LayoutFullWidthPage } from 'layouts/full-width-page'

import { GetVisitorTypeRequest } from 'generated/mos/org/getty/audioaccess'
import { VisitorType } from 'generated/mos/org/getty/audio'
import { UpdateVisitorTypeRequest } from 'generated/mos/org/getty/audiomanagement'
import { Language } from 'generated/mos/i18n'

import { pick } from 'helpers/core'
import { statusSelector, Status } from 'helpers/status'
import { space } from 'helpers/style'
import { translateValues, TranslationFormBags } from 'helpers/schema'
import { defaultLanguage, isDefaultLanguage, parseLanguage } from 'helpers/i18n'
import { getResolvedValue } from 'helpers/linkables'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { getVisitorTypeSubnav } from '../subnav'

import { LANGUAGES, LanguageCode } from '../../../constants'
import { Input } from 'components/ui/input'

type ActionProps =
  Pick<GettyActionCreators, 'gettyGetVisitorTypeRequest' | 'gettyUpdateVisitorTypeRequest' > &
  Pick<typeof sharedActionCreators, 'toastNotification'>;

type ConnectedProps = Pick<GettyStore['getty'], 'visitorTypeUpdate'>;

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly visitorTypeId: string;
};

type Props = ActionProps & ConnectedProps & DirectProps;

type FormValues = {
  label: string | undefined;
}

type FormProps = {
  language: Language.Entity;
  formBag: FormBag<FormValues> | undefined;
  setFormBag: (bag: FormBag<FormValues>) => void;
}

const HEADER_HEIGHT = 100

const Scroll = styled.div`
  display: flex;
  padding-top: ${space(6)};
  padding-bottom: ${space(15)};
  overflow-x: scroll;
  height: calc(100vh - ${(NAVBAR_HEIGHT + FOOTER_HEIGHT + HEADER_HEIGHT)}px);

`
const Panels = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  padding-left: ${space(9)};
  padding-right: ${space(9)};


  ${mediaQueries.lap} {
    padding-left: ${space(18)};
    padding-right: ${space(18)};
  }

  @media only screen and (min-width: 1600px) {
    padding-left: calc(${space(18)} + (100% - 1600px) / 2);
    padding-right: calc(${space(18)} + (100% - 1600px) / 2);
  }
`

const Panel = styled.div`
  width: calc(100vw - 36px);
  flex: 0 0 auto;
  &:not(:last-child) {
    padding-right: ${space(6)};
    margin-right: ${space(6)};
    border-right: 1px solid ${({ theme }) => theme.color.grayL60};
  }

  ${mediaQueries.hand} {
    width: 500px;
  }
`

const TranslationForm = (props: FormProps) => {
  const { formBag, language, setFormBag } = props

  return formBag ? (
    <Panel>
      <FormData
        bag={formBag}
        onUpdate={(event) => {
          setFormBag(event.bag)
        }}
        validate={() => ({})}
        >
        <Fieldset legend={parseLanguage(language)}>
          <Input
            label="Label"
            field="label"
            isDisabled={isDefaultLanguage(language)}
          />
        </Fieldset>
      </FormData>
    </Panel>
  ) : null
}

const VisitorTypeTranslationsView = (props: Props) => {
  const {
    gettyGetVisitorTypeRequest ,
    gettyUpdateVisitorTypeRequest,
    historyPush,
    projectId,
    visitorTypeId,
    visitorTypeUpdate,
    toastNotification,
  } = props;

  // set up form values
  const [ initialValues, setInitialValues ] = useState<TranslationFormBags<FormValues, LanguageCode> | undefined>(undefined);
  const [ formBags, setFormBags ] = useState<TranslationFormBags<FormValues, LanguageCode> | undefined>(undefined);

  const backLocation = `/project/${projectId}/visitor-types`;

  const visitorTypeUpdateData = statusSelector.data(visitorTypeUpdate);
  const visitorType = visitorTypeUpdateData && visitorTypeUpdateData.visitorType;

  const hasSubmitted = useRef(false)

  // load initial data
  useEffect(() => {
    if (visitorTypeUpdate.status === Status.Idle || (visitorType && VisitorType.mustRef(visitorType.ref).id !== visitorTypeId)) {
      gettyGetVisitorTypeRequest({
        type: GetVisitorTypeRequest.refName,
        visitorTypeRef: {
          typename: 'mos.org.getty.audio.VisitorType',
          id: visitorTypeId,
        }
      })
    }
  })


  useEffect(() => {

    // only proceed if stop data is available and status is ready
    if (!visitorType || visitorTypeUpdate.status !== Status.Ready) return;

    const getInitialValues = () => {
      const initialValues = LANGUAGES.reduce(
        (acc, curr) => {

          const label = visitorType.label ? getResolvedValue(visitorTypeSchema.label.read(visitorType.label, curr), []) : ''

          return {
            ...acc,
            [curr.languageCode]: createFormBag({
              label,
            })
          }
        // eslint-disable-next-line @typescript-eslint/no-object-literal-type-assertion
        }, {} as TranslationFormBags<FormValues, LanguageCode>)
      return initialValues
    }

    const initValues = getInitialValues()
    setInitialValues(initValues)
    setFormBags(initValues)

  }, [visitorType, visitorTypeUpdate.status]) // eslint-disable-line react-hooks/exhaustive-deps


  // if the data has been submitted, trigger success toast.
  useEffect(() => {
    if (visitorTypeUpdate.status === Status.Ready && hasSubmitted.current) {
      toastNotification({ type: 'success', text: 'Translations updated' })
      hasSubmitted.current = false
    }
  },[hasSubmitted, visitorTypeUpdate.status, toastNotification])

  /**
   * Determine if current form state is dirty
   * Iterate through through the form values for each translation language
   * and return true as soon as it finds a value that has been changed
   * @returns boolean
   */
  const areValuesModified = () => {
    // don't continue unless initial values and formbag values exist
    if (!initialValues || !formBags) return;

    for (const value of LANGUAGES) {
      const languageCode: LanguageCode = value.languageCode as LanguageCode

      const values = {
        initial: {...initialValues[languageCode].values},
        current: {...formBags[languageCode].values}
      }

      if (
        values.initial.label !== values.current.label
      ) return true;
    }

    return false;
  }

  const modified = areValuesModified()


  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {}
    return errors
  }

  const validate = (formBags: TranslationFormBags<FormValues, LanguageCode>): boolean => {
    let valid = true;
    LANGUAGES.forEach((value) => {
      const languageCode: LanguageCode = value.languageCode as LanguageCode
      const bag = validateFormBag(formBags[languageCode], validateForm);
      valid = bag.valid
      updateFormBags(languageCode as LanguageCode)(bag)
    })
    return valid
  }

  /**
   * The default language values for each field are resolved to string in the form fields
   * but they could really be be entity references. Before saving updated translation strings
   * we need to copy the original values for the default language back into the array
   * of translated values so that we don't accidentally overwrite entity reference
   * values with resolved strings in the form data
   * @param field name of the field to operate on
   * @param translatedValues the function returned by translateValues for field lookup
   * @param stop Stop.Entity which includes the original values we need to retrieve
   * @returns
   */
  const insertOriginalDefaultLanguageValues = <K extends keyof VisitorType.Entity>(
    field: K,
    translatedValues: (key: K) => any[],
    visitorType: VisitorType.Entity
  ) => {
    // Look in the raw stop data received from the server to
    // get the default language's unmodified  values for the field
    const defaultLanguageValues = visitorTypeSchema[field].read(visitorType[field], defaultLanguage());

    // get an array of translated values for the specific field we're interested in
    // from the formbag data (values entered by the user in the UI)
    const translations = translatedValues(field);

    // find the index for the default language in the translated values from the form
    // ** these are the values we want to replace **
    const index = translations.findIndex(lang => isDefaultLanguage(lang.language))

    // if the original data contained values for the default language
    // smoosh them back into th translated values data
    // or if they somehow aren't preent in the translated values, append them to the end
    if (defaultLanguageValues && index >=0) {
      const insertAt = index >= 0 ? index : translations.length;
      translations[index].stringValue = defaultLanguageValues
    }

    // return the array containing the form data and the merged original values for default language
    return translations;
  }

  const submit = (formBags: TranslationFormBags<FormValues, LanguageCode>, visitorType: VisitorType.Entity) => {
    // Validate all bags
    const valid = validate(formBags)

    // Check if any bags a invalid
    if(!valid) return false;

    const translatedValues = translateValues<VisitorType.Entity, FormValues, LanguageCode>(visitorTypeSchema, visitorType, LANGUAGES, formBags)

    const label = insertOriginalDefaultLanguageValues('label', translatedValues, visitorType);

    // Update Stop with changes.
    gettyUpdateVisitorTypeRequest({
      ...UpdateVisitorTypeRequest.defaults,
      visitorTypeRef: VisitorType.mustRef(visitorType.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: ['label'],
      },
      label: {
        type: "mos.translatable.TranslatableStringValue",
        translatedValues: label
      },
    })

    hasSubmitted.current = true

    return false;
  };

  const updateFormBags= (languageCode: LanguageCode) =>  (bag: FormBag<FormValues>) => {
    if (!formBags) return;
    setFormBags({ ...formBags, [languageCode]: bag })
  }

  const title = visitorType && visitorType.tag && visitorTypeSchema.tag ? visitorTypeSchema.tag.read(visitorType.tag) as string : ''

  return (
    <>
      <LayoutFullWidthPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title={`Translate visitor type "${title}"`}
            onConfirm={modified ? () => historyPush(backLocation) : undefined}
            navBackRoute={backLocation}
          />
        )}
        subnav={() => (
          <SecondaryNavigation navItems={getVisitorTypeSubnav(projectId, visitorTypeId)} modified={modified} historyPush={historyPush} >
          </SecondaryNavigation>
        )}
        title={title}
        content={() => (
          <StatusGuard status={visitorTypeUpdate} mode="form">
            <Scroll>
              <Panels>
                {formBags && LANGUAGES.map((value) => {
                  const languageCode: LanguageCode = value.languageCode as LanguageCode
                  return (
                    <TranslationForm key={languageCode} language={value} formBag={formBags[languageCode]} setFormBag={updateFormBags(languageCode)} />
                  )
                })}
              </Panels>
            </Scroll>
          </StatusGuard>
        )}
        footer={() => formBags && visitorType && (
          <FooterEditEntity
            entity={visitorTypeUpdate}
            modified={modified}
            onCancel={() => historyPush(backLocation)}
            onSave={() => {
              submit(formBags, visitorType);
              return false;
            }}
          />
        )}
      />
    </>
  )
}

export const VisitorTypeTranslations = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  store => pick(store.getty, 'visitorTypeUpdate'),
  {
    ...pick(actionCreators, 'gettyGetVisitorTypeRequest', 'gettyUpdateVisitorTypeRequest'),
    ...pick(sharedActionCreators, 'toastNotification'),
  },
)(VisitorTypeTranslationsView);
